/**
 * Where you can go.
 *
 * One list, read by the sidebar, the bottom bar and the command palette, so
 * the three cannot disagree about what exists. Order is the order of the day:
 * what needs a decision, then where the work is, then when it goes out, then
 * who it is for and who is doing it, then the numbers, then the settings.
 *
 * `partnerOnly` is a courtesy, not a control. It keeps a link out of the
 * sidebar for somebody who would only get a 403 by following it; the server
 * decides, independently, on every request.
 */

import {
  Building2,
  CalendarDays,
  FolderOpen,
  Kanban,
  Layers,
  ListChecks,
  Package,
  LineChart,
  Settings,
  ShieldCheck,
  SquareKanban,
  Target,
  Telescope,
  Users
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Shorter, for the 20%-of-a-phone-wide slot in the bottom bar. */
  short: string;
  partnerOnly?: true;
  /** Kept out of the bottom bar — five is what fits on a phone. */
  desktopOnly?: true;
}

export const navItems: NavItem[] = [
  { label: 'Today', to: '/today', icon: Target, short: 'Today' },
  { label: 'Board', to: '/board', icon: Kanban, short: 'Board' },
  { label: 'Tasks', to: '/tasks', icon: ListChecks, short: 'Tasks' },
  {
    label: 'Projects',
    to: '/projects',
    icon: Layers,
    short: 'Projects',
    desktopOnly: true
  },
  { label: 'Calendar', to: '/calendar', icon: CalendarDays, short: 'Calendar', desktopOnly: true },
  { label: 'Clients', to: '/clients', icon: Building2, short: 'Clients' },
  { label: 'Prospects', to: '/prospects', icon: Telescope, short: 'Prospects', desktopOnly: true },
  {
    label: 'Content plan',
    to: '/content',
    icon: SquareKanban,
    short: 'Content',
    desktopOnly: true
  },
  {
    label: 'Documents',
    to: '/documents',
    icon: FolderOpen,
    short: 'Docs',
    desktopOnly: true
  },
  {
    label: 'Buckets',
    to: '/buckets',
    icon: Package,
    short: 'Buckets',
    desktopOnly: true
  },
  { label: 'Team', to: '/people', icon: Users, short: 'Team' },
  {
    label: 'The month',
    to: '/month',
    icon: LineChart,
    short: 'Month',
    partnerOnly: true,
    desktopOnly: true
  },
  { label: 'Settings', to: '/settings', icon: Settings, short: 'Settings', desktopOnly: true },
  {
    label: 'Admin',
    to: '/admin',
    icon: ShieldCheck,
    short: 'Admin',
    partnerOnly: true,
    desktopOnly: true
  }
];

/** The five that fit across the bottom of a phone. */
export const mobileNavItems: NavItem[] = navItems.filter((i) => i.desktopOnly !== true);

export function navFor(role: 'partner' | 'team' | 'viewer'): NavItem[] {
  return navItems.filter((i) => i.partnerOnly !== true || role === 'partner');
}
